import { gql, TypedDocumentNode } from "@apollo/client";
import {
  employeeReferenceFragment,
  newReferenceFragment,
  parentReferenceFragment,
} from "./references";
import { projectListFields } from "./newProject";
import type {
  GetEmployeeQuery,
  GetEmployeeQueryVariables,
  Employee,
  EditEmployeeQueryVariables,
  EditEmployeeQuery,
  EditEmployeeMutationMutation,
  EditEmployeeMutationMutationVariables,
  AllEmployeesQueryQuery,
  AllEmployeesQueryQueryVariables,
  AllEmployeesWithRoleQueryQuery,
  AllEmployeesWithRoleQueryQueryVariables,
  OnepagerEmployeeQuery,
  OnepagerEmployeeQueryVariables,
  NewProject,
} from "generated/graphql";

export const employeeFragment: TypedDocumentNode<Employee> = gql`
  fragment Employee_fields on Employee {
    firstName
    lastName
    fullName
    activeDirectoryId
    profilePicture(size: 240)
    colorProfilePicture(size: 504)
    phone
    birthday
    email
    linkedin
    services
    domains
    isTrustedAdvisor
    isFormerEmployee
    msMail
    msTbfEntryDate
    msTbfSeparationDate
    roles {
      id
      roleId
      newText
      sourceLanguage
      role {
        id
        name {
          id
          de
          en
          fr
          it
        }
      }
    }
    skills {
      id
      skillId
      newText
      sourceLanguage
      skill {
        id
        name {
          id
          de
          en
          fr
          it
        }
      }
    }
    competences {
      id
      competenceId
      newText
      sourceLanguage
      competence {
        id
        name {
          id
          de
          en
          fr
          it
        }
      }
    }
    aboutMe {
      id
      de
      en
      fr
      it
    }
    trainings {
      id
      de
      en
      fr
      it
    }
    languages {
      id
      level
      languageId
      language {
        id
        name {
          id
          de
          en
          fr
          it
        }
      }
    }
    diplomas {
      id
      year
      diplomaId
      newText
      sourceLanguage
      diploma {
        id
        name {
          id
          de
          en
          fr
          it
        }
      }
    }
    additionalField {
      id
      title {
        id
        de
        en
        fr
        it
      }
      content {
        id
        de
        en
        fr
        it
      }
    }
    cv {
      id
      from
      to
      description {
        id
        de
        en
        fr
        it
      }
      institution {
        id
        de
        en
        fr
        it
      }
    }
    isReadyForReview
  }
`;

export const employeeRelationsFragment: TypedDocumentNode<NewProject> = gql`
  ${projectListFields}

  fragment EmployeeRelations_fields on Employee {
    projects {
      ...ProjectList_fields
    }
  }
`;

export const EMPLOYEE_QUERY: TypedDocumentNode<
  GetEmployeeQuery,
  GetEmployeeQueryVariables
> = gql`
  ${employeeFragment}
  ${employeeRelationsFragment}
  ${newReferenceFragment}
  ${parentReferenceFragment}

  query GetEmployee(
    $id: ID
    $activeDirectoryId: String
    $abacusId: ID
    $employeeNumber: ID
  ) {
    me {
      id
    }
    employee(
      id: $id
      activeDirectoryId: $activeDirectoryId
      abacusId: $abacusId
      employeeNumber: $employeeNumber
    ) {
      id
      ...Employee_fields
      ...EmployeeRelations_fields
      references(includeUnapproved: true) {
        ...NewReference_fields
        project {
          id
          isUnlisted
          parentProjects {
            id
            name {
              id
              de
              en
              fr
              it
            }
          }
        }
        parentReference {
          ...ParentReference_fields
        }
        draft {
          ...NewReference_fields
          project {
            id
            isUnlisted
            parentProjects {
              id
              name {
                id
                de
                en
                fr
                it
              }
            }
          }
          parentReference {
            ...ParentReference_fields
          }
        }
      }
      connectedFields {
        field
        isConnected
      }
      approvedBy {
        __typename
      }
      approvedEntityId
      draft {
        id
        ...Employee_fields
        ...EmployeeRelations_fields
        connectedFields {
          field
          isConnected
        }
        modifiedBy {
          __typename
        }
      }
      countOpenDraftsCreatedByMe(status: "notReady")
    }
  }
`;

export const EDIT_EMPLOYEE_QUERY: TypedDocumentNode<
  EditEmployeeQuery,
  EditEmployeeQueryVariables
> = gql`
  ${employeeFragment}
  ${employeeReferenceFragment}

  query EditEmployee($id: ID!) {
    employee(id: $id) {
      id
      ...Employee_fields
      references {
        ...EmployeeReference_fields
      }
      draft {
        id
        ...Employee_fields
        references {
          ...EmployeeReference_fields
        }
        connectedFields {
          field
          isConnected
        }
        modifiedBy {
          __typename
        }
      }
      connectedFields {
        field
        isConnected
      }
      approvedBy {
        __typename
      }
    }
  }
`;

export const EDIT_EMPLOYEE_MUTATION: TypedDocumentNode<
  EditEmployeeMutationMutation,
  EditEmployeeMutationMutationVariables
> = gql`
  ${employeeFragment}

  mutation EditEmployeeMutation($id: ID!, $fields: InputEmployee!) {
    editEmployee(id: $id, fields: $fields) {
      id
      ...Employee_fields
      approvedBy {
        __typename
      }
      draft {
        id
        ...Employee_fields
        modifiedBy {
          __typename
        }
      }
    }
  }
`;

export const ALL_EMPLOYEES_QUERY: TypedDocumentNode<
  AllEmployeesQueryQuery,
  AllEmployeesQueryQueryVariables
> = gql`
  query allEmployeesQuery {
    employees {
      id
      firstName
      lastName
      isFormerEmployee
    }
  }
`;

export const ALL_EMPLOYEES_WITH_ROLE_QUERY: TypedDocumentNode<
  AllEmployeesWithRoleQueryQuery,
  AllEmployeesWithRoleQueryQueryVariables
> = gql`
  query allEmployeesWithRoleQuery {
    employees {
      id
      firstName
      lastName
      colorProfilePicture(size: 504)
      isFormerEmployee
      roles {
        id
        roleId
        role {
          id
          name {
            id
            de
            en
            fr
            it
          }
        }
      }
    }
  }
`;

export const ONEPAGER_EMPLOYEE_QUERY: TypedDocumentNode<
  OnepagerEmployeeQuery,
  OnepagerEmployeeQueryVariables
> = gql`
  query OnepagerEmployee($id: ID!) {
    employee(id: $id) {
      id
      phone
      email
    }
  }
`;
